<template>
  <div ref="videoPlayer">
    <div
      @click="playVideo"
      v-show="visibilityPlayPauseButton"
      class="play-pause-button"
    >
      <i class="icon icon-video-play"></i>
    </div>
    <video
      ref="video"
      :muted="isMute"
      @click="stopVideo"
      @timeupdate="updateProgress"
      @ended="finisheVideo"
      @loadeddata="$emit('videoLoaded')"
    >
      <source :src="videoUrl" type="video/mp4" />
    </video>
    <div class="controls">
      <div class="progress-bar">
        <input
          ref="marker"
          :value="currentTime"
          min="0"
          :max="videoDuration"
          type="range"
          step="1"
          @input="changeVideoTime"
        />
        <progress
          ref="progress"
          :value="currentTime"
          min="0"
          :max="videoDuration"
        ></progress>
      </div>
      <div>
        <span>
          <span>
            <button @click="playPauseVideo">
              <i v-if="!playing" class="icon icon-video-play"></i>
              <i v-else class="icon icon-video-pause"></i>
            </button>
          </span>
          <span>
            <button @click="switchVolume">
              <i v-if="!isMute" class="icon icon-volume-high"></i>
              <i v-else class="icon icon-volume-mute"></i>
            </button>
          </span>
          <span class="time">
            <span class="dark">{{ currentVideoTimeString }}</span>
            <span> / </span>
            <span>{{ videoDurationString }}</span>
          </span>
        </span>
        <span class="right-buttons">
          <span>
            <button @click="switchFullScreen">
              <i v-if="!isFullScreen" class="icon icon-resize-up"></i>
              <i v-else class="icon icon-resize-down"></i>
            </button>
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoPlayer',
  props: {
    videoUrl: {
      type: String,
      default: null,
    },
  },
  mounted: function () {
    this.video = this.$refs.video;
    this.progress = this.$refs.progress;
    this.marker = this.$refs.marker;
    this.videoPlayer = this.$refs.videoPlayer;

    this.video.onloadedmetadata = () => {
      this.videoDuration = Math.floor(this.video.duration);
    };
  },
  methods: {
    updateProgress() {
      this.currentTime = Math.floor(this.video.currentTime);
    },
    finisheVideo() {
      this.stopVideo();
      this.currentTime = 0;
    },
    changeVideoTime(event) {
      const newCurrentTime = event.target.dataset.seek
        ? event.target.dataset.seek
        : event.target.value;

      this.video.currentTime = newCurrentTime;
      this.progress.value = newCurrentTime;
      this.marker.value = newCurrentTime;

      this.$emit('videoSeeked');
    },
    playVideo() {
      if (this.video.paused || this.video.ended) {
        this.visibilityPlayPauseButton = false;
        this.playing = true;
        this.video.play();

        this.video.currentTime === 0
          ? this.$emit('videoStarted')
          : this.$emit('videoPlayed');
      }
    },
    stopVideo() {
      if ((this.video.played && !this.video.paused) || this.video.ended) {
        this.video.pause();
        this.playing = false;
        this.visibilityPlayPauseButton = true;

        this.video.ended && this.currentTime != 0
          ? this.$emit('videoFinished')
          : this.$emit('videoPaused');
      }
    },
    playPauseVideo() {
      if (this.video.paused || this.video.ended) {
        this.playVideo();
      } else if (this.video.played) {
        this.stopVideo();
      }
    },
    getTimeInString(timeInSeconds) {
      let seconds = String(timeInSeconds % 60);
      let minutes = String((timeInSeconds - seconds) / 60);

      return (
        (minutes.length > 1 ? minutes : '0' + minutes) +
        ':' +
        (seconds.length > 1 ? seconds : '0' + seconds)
      );
    },
    switchFullScreen() {
      if (this.isFullScreen) {
        this.cancelFullScreen();
        this.isFullScreen = false;
      } else {
        this.openFullScreen();
        this.isFullScreen = true;
      }
    },
    openFullScreen() {
      if (this.videoPlayer.requestFullScreen) {
        this.videoPlayer.requestFullScreen();
      } else if (this.videoPlayer.webkitRequestFullScreen) {
        this.videoPlayer.webkitRequestFullScreen();
      } else if (this.videoPlayer.mozRequestFullScreen) {
        this.videoPlayer.mozRequestFullScreen();
      } else if (this.videoPlayer.msRequestFullScreen) {
        this.videoPlayer.msRequestFullScreen();
      }
    },
    cancelFullScreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullScreen) {
        document.webkitExitFullScreenc();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    switchVolume() {
      if (this.isMute) {
        this.isMute = false;
        this.$emit('soundUnmuted');
      } else {
        this.isMute = true;
        this.$emit('soundMuted');
      }
    },
  },
  computed: {
    videoDurationString() {
      return this.getTimeInString(this.videoDuration);
    },
    currentVideoTimeString() {
      return this.getTimeInString(this.currentTime);
    },
  },
  data() {
    return {
      visibilityPlayPauseButton: true,
      isFullScreen: false,
      isMute: false,
      playing: false,

      videoDuration: 0,
      currentTime: 0,
    };
  },
};
</script>

<style lang="less" scoped>
@import '~@eds/vanilla/variables/light.less';
@import '~@eds/vanilla/variables/global.less';

video {
  min-width: 854px;
  min-height: 480px;
  width: 100%;
  height: calc(100% - 38px);
  display: block;
  border: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
}
.play-pause-button {
  background-color: rgba(72, 72, 72, 0.5);
  cursor: pointer;
  border-radius: 5%;
  position: absolute;
  z-index: 30;
  width: 90px;
  height: 60px;
  font-size: 250%;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
button {
  background: none;
  color: rgba(187, 187, 187);
  cursor: pointer;
  border: 0 !important;
  margin-top: 5px;
  margin-bottom: 5px;
  margin-left: 10px;
  margin-right: 3px;
}
.time {
  margin-top: 5px;
  margin-bottom: 5px;
  margin-left: 10px;
  color: rgba(187, 187, 187);
  cursor: default;
}
.controls {
  position: relative;
  z-index: 30;
  bottom: 0;
  background-color: rgb(19, 19, 19);
  width: 100%;
  margin: 0 !important;
  border: 0 !important;
  padding: 0 !important;
}
.right-buttons {
  float: right;
}
.progress-bar {
  position: relative;
}
progress {
  display: block;
  width: 100% !important;
  height: 9px !important;
  border: 0;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
}
progress::-webkit-progress-bar {
  background-color: rgba(78, 78, 78) !important;
}
progress::-webkit-progress-value {
  background-color: rgba(4, 130, 238) !important;
  background-image: none !important;
}
input[type='range'] {
  position: absolute !important;
  width: calc(100% + 6px) !important;
  transform: translate(-3px, -3px);
  margin: 0 !important;
  border: 0 !important;
  padding: 0 !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  background: transparent;
}
input[type='range']::-webkit-slider-runnable-track {
  -webkit-appearance: none !important;
  cursor: pointer;
}
input[type='range']::-webkit-slider-thumb {
  background: white;
  cursor: pointer;
  width: 15px;
  height: 15px;
  border-radius: 50px;
  -webkit-appearance: none;
}
</style>